import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import {
  Title, Card, Table, Button, Group, Text, Modal,
  NumberInput, TextInput, Textarea, Grid, Stack, Loader, Center,
  ActionIcon, Tooltip, Badge, Divider, Switch,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconPlus, IconPencil, IconTrash, IconCar,
  IconCircleCheck, IconCircleDashed,
} from '@tabler/icons-react';
import 'dayjs/locale/it';

function formatAmount(v) {
  if (!v && v !== 0) return '—';
  return '€ ' + Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatMese(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
}

function meseToStr(d) {
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function IvaHint({ value }) {
  if (!value) return null;
  const lordo = Number(value) * 1.22;
  return (
    <Text size="xs" c="dimmed" mt={2}>
      Lordo IVA 22%: € {lordo.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </Text>
  );
}

function ReconcIcon({ items, fieldRef, onToggle }) {
  const checked = (items || []).some(i => i.field_ref === fieldRef);
  return (
    <Tooltip label={checked ? 'Riconciliato manualmente' : 'Segna come riconciliato'}>
      <ActionIcon variant="subtle" size="sm" onClick={() => onToggle(fieldRef)} style={{ marginLeft: 4 }}>
        {checked
          ? <IconCircleCheck size={16} color="#2b8a3e" />
          : <IconCircleDashed size={16} color="#adb5bd" />
        }
      </ActionIcon>
    </Tooltip>
  );
}

const EMPTY_FORM = {
  mese_riferimento: null,
  targa: '', telaio: '', marca: '', modello: '',
  venditore_denominazione: '',
  acquirente_denominazione: '', acquirente_indirizzo: '', acquirente_cf_piva: '', acquirente_cellulare: '', acquirente_email: '',
  prezzo_acquisto_veicolo: 0, prezzo_vendita_veicolo: 0,
  prezzo_acquisto_pratiche: 0, prezzo_vendita_pratiche: 0,
  costi_accessori: 0,
  provvigioni_attive: 0, provvigioni_passive: 0,
  finanziamento_attivo: false, finanziaria: '', importo_finanziato: 0,
  consegnata: false,
  note: '',
  riconciliazioni: [],
};

export default function CompravenditaV2() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroMese, setFiltroMese] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  useEffect(() => { loadRecords(); }, [filtroMese]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroMese) params.mese = meseToStr(filtroMese);
      const res = await api.get('/compravendita-v2', { params });
      setRecords(res.data);
    } catch {
      notifications.show({ title: 'Errore', message: 'Impossibile caricare i dati', color: 'red' });
    } finally { setLoading(false); }
  };

  const resetForm = () => { setForm({ ...EMPTY_FORM }); setEditingRecord(null); };

  const openNew = () => { resetForm(); setShowModal(true); };

  const openEdit = (r) => {
    setEditingRecord(r);
    setForm({
      mese_riferimento: r.mese_riferimento ? new Date(r.mese_riferimento) : null,
      targa: r.targa || '', telaio: r.telaio || '',
      marca: r.marca || '', modello: r.modello || '',
      venditore_denominazione: r.venditore_denominazione || '',
      acquirente_denominazione: r.acquirente_denominazione || '',
      acquirente_indirizzo: r.acquirente_indirizzo || '',
      acquirente_cf_piva: r.acquirente_cf_piva || '',
      acquirente_cellulare: r.acquirente_cellulare || '',
      acquirente_email: r.acquirente_email || '',
      prezzo_acquisto_veicolo: r.prezzo_acquisto_veicolo || 0,
      prezzo_vendita_veicolo: r.prezzo_vendita_veicolo || 0,
      prezzo_acquisto_pratiche: r.prezzo_acquisto_pratiche || 0,
      prezzo_vendita_pratiche: r.prezzo_vendita_pratiche || 0,
      costi_accessori: r.costi_accessori || 0,
      provvigioni_attive: r.provvigioni_attive || 0,
      provvigioni_passive: r.provvigioni_passive || 0,
      finanziamento_attivo: r.finanziamento_attivo || false,
      finanziaria: r.finanziaria || '',
      importo_finanziato: r.importo_finanziato || 0,
      consegnata: r.consegnata || false,
      note: r.note || '',
      riconciliazioni: r.riconciliazioni || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Eliminare la compravendita ${r.marca || ''} ${r.modello || ''} (${formatMese(r.mese_riferimento)})?`)) return;
    try {
      await api.delete(`/compravendita-v2/${r.id}`);
      notifications.show({ title: 'Eliminato', message: 'Record eliminato', color: 'green' });
      loadRecords();
    } catch {
      notifications.show({ title: 'Errore', message: "Errore nell'eliminazione", color: 'red' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.mese_riferimento) {
      notifications.show({ title: 'Attenzione', message: 'Seleziona il mese di riferimento', color: 'orange' });
      return;
    }
    try {
      const payload = {
        ...form,
        mese_riferimento: meseToStr(form.mese_riferimento),
        targa: form.targa || null,
        telaio: form.telaio || null,
        marca: form.marca || null,
        modello: form.modello || null,
        venditore_denominazione: form.venditore_denominazione || null,
        acquirente_denominazione: form.acquirente_denominazione || null,
        acquirente_indirizzo: form.acquirente_indirizzo || null,
        acquirente_cf_piva: form.acquirente_cf_piva || null,
        acquirente_cellulare: form.acquirente_cellulare || null,
        acquirente_email: form.acquirente_email || null,
        finanziaria: form.finanziaria || null,
        importo_finanziato: form.importo_finanziato || 0,
        note: form.note || null,
      };
      if (editingRecord) {
        await api.put(`/compravendita-v2/${editingRecord.id}`, payload);
        notifications.show({ title: 'Salvato', message: 'Compravendita aggiornata', color: 'green' });
      } else {
        await api.post('/compravendita-v2', payload);
        notifications.show({ title: 'Creato', message: 'Compravendita aggiunta', color: 'green' });
      }
      setShowModal(false);
      resetForm();
      loadRecords();
    } catch (err) {
      notifications.show({ title: 'Errore', message: err.response?.data?.detail || 'Errore nel salvataggio', color: 'red' });
    }
  };

  // Riconciliazione manuale: toggle di un singolo field_ref
  const toggleReconc = (fieldRef) => {
    setForm(prev => {
      const already = (prev.riconciliazioni || []).some(i => i.field_ref === fieldRef);
      const others = (prev.riconciliazioni || []).filter(i => i.field_ref !== fieldRef);
      return { ...prev, riconciliazioni: already ? others : [...others, { field_ref: fieldRef, tipo: 'manuale' }] };
    });
  };

  const calcMargine = (f) => {
    return (
      (f.prezzo_vendita_veicolo || 0) - (f.prezzo_acquisto_veicolo || 0) +
      (f.prezzo_vendita_pratiche || 0) - (f.prezzo_acquisto_pratiche || 0) -
      (f.costi_accessori || 0) - (f.provvigioni_passive || 0) + (f.provvigioni_attive || 0)
    );
  };

  const isFullyReconciled = (r) => {
    const ric = r.riconciliazioni || [];
    const fields = [
      'prezzo_acquisto_veicolo', 'prezzo_vendita_veicolo',
      'prezzo_acquisto_pratiche', 'prezzo_vendita_pratiche',
      'costi_accessori', 'provvigioni_attive', 'provvigioni_passive',
    ];
    if (r.finanziamento_attivo) fields.push('importo_finanziato');
    const nonZeroFields = fields.filter(f => (r[f] || 0) !== 0);
    if (nonZeroFields.length === 0) return false;
    return nonZeroFields.every(f => ric.some(i => i.field_ref === f));
  };

  const handleToggleConsegnata = async (r) => {
    try {
      await api.patch(`/compravendita-v2/${r.id}/consegnata`);
      setRecords(prev => prev.map(rec => rec.id === r.id ? { ...rec, consegnata: !rec.consegnata } : rec));
    } catch {
      notifications.show({ title: 'Errore', message: 'Impossibile aggiornare lo stato consegna', color: 'red' });
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm">
          <IconCar size={28} color="#205357" stroke={1.5} />
          <Title order={2} style={{ color: '#205357' }}>Compravendita</Title>
        </Group>
        <Button leftSection={<IconPlus size={16} />} color="teal" onClick={openNew} fullWidth={isMobile}>
          Nuova compravendita
        </Button>
      </Group>

      <Card shadow="sm" radius="md" withBorder>
        <Group mb="md" w="100%">
          <MonthPickerInput
            placeholder="Filtra per mese"
            value={filtroMese}
            onChange={setFiltroMese}
            clearable
            size="sm"
            locale="it"
            style={{ flex: 1, minWidth: 160 }}
          />
          {filtroMese && (
            <Button variant="subtle" size="xs" color="gray" onClick={() => setFiltroMese(null)}>
              Rimuovi filtro
            </Button>
          )}
        </Group>

        {loading ? (
          <Center h={200}><Loader /></Center>
        ) : records.length === 0 ? (
          <Center h={100}><Text c="dimmed">Nessuna compravendita trovata</Text></Center>
        ) : (
          <Table.ScrollContainer minWidth={isMobile ? 0 : 800}>
          <Table striped highlightOnHover withTableBorder withColumnBorders style={{ fontSize: isMobile ? '0.8rem' : 13 }}>
            <Table.Thead>
              <Table.Tr>
                {!isMobile && <Table.Th>Mese</Table.Th>}
                <Table.Th>Targa</Table.Th>
                {!isMobile && <Table.Th>Veicolo</Table.Th>}
                {!isMobile && <Table.Th>Venditore</Table.Th>}
                <Table.Th>Acquirente</Table.Th>
                {!isMobile && <Table.Th style={{ textAlign: 'right' }}>Finanziamento</Table.Th>}
                <Table.Th style={{ textAlign: 'right' }}>Margine</Table.Th>
                {!isMobile && <Table.Th>Riconciliazioni</Table.Th>}
                {!isMobile && <Table.Th style={{ textAlign: 'center' }}>Consegnata</Table.Th>}
                <Table.Th w={isMobile ? 60 : 80}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {records.map(r => {
                const margine = calcMargine(r);
                const ricLinked = (r.riconciliazioni || []).length;
                return (
                  <Table.Tr key={r.id} onClick={() => isMobile && openEdit(r)} style={isMobile ? { cursor: 'pointer' } : undefined}>
                    {!isMobile && <Table.Td>{formatMese(r.mese_riferimento)}</Table.Td>}
                    <Table.Td>
                      <Text size={isMobile ? 'xs' : 'sm'} fw={500}>{r.targa || '—'}</Text>
                      {isMobile && (
                        <Text size="xs" c="dimmed">{[r.marca, r.modello].filter(Boolean).join(' ') || ''}</Text>
                      )}
                    </Table.Td>
                    {!isMobile && <Table.Td>{[r.marca, r.modello].filter(Boolean).join(' ') || '—'}</Table.Td>}
                    {!isMobile && <Table.Td>{r.venditore_denominazione || '—'}</Table.Td>}
                    <Table.Td style={isMobile ? { maxWidth: 100 } : undefined}>
                      <Text size={isMobile ? 'xs' : 'sm'} truncate={isMobile ? 'end' : undefined}>
                        {r.acquirente_denominazione || '—'}
                      </Text>
                    </Table.Td>
                    {!isMobile && (
                      <Table.Td style={{ textAlign: 'right' }}>
                        {r.finanziamento_attivo && r.importo_finanziato
                          ? <Text size="sm" fw={500}>{formatAmount(r.importo_finanziato)}</Text>
                          : <Text size="sm" c="dimmed">—</Text>
                        }
                      </Table.Td>
                    )}
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size={isMobile ? 'xs' : 'sm'} fw={600} c={margine >= 0 ? 'teal' : 'red'}>{formatAmount(margine)}</Text>
                      {isMobile && ricLinked > 0 && (
                        <Badge size="xs" color="teal" variant="light" mt={2}>{ricLinked} fatt.</Badge>
                      )}
                    </Table.Td>
                    {!isMobile && (
                      <Table.Td>
                        {isFullyReconciled(r)
                          ? <Badge size="xs" color="green" variant="filled" leftSection={<IconCircleCheck size={12} />}>Tutto riconciliato</Badge>
                          : ricLinked > 0
                            ? <Badge size="xs" color="teal" variant="light">{ricLinked} fattura/e</Badge>
                            : <Text size="xs" c="dimmed">—</Text>
                        }
                      </Table.Td>
                    )}
                    {!isMobile && (
                      <Table.Td style={{ textAlign: 'center' }}>
                        <Switch
                          size="xs"
                          color="teal"
                          checked={r.consegnata || false}
                          onChange={(e) => { e.stopPropagation(); handleToggleConsegnata(r); }}
                        />
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Tooltip label="Modifica">
                          <ActionIcon variant="subtle" color="blue" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); openEdit(r); }}>
                            <IconPencil size={isMobile ? 14 : 16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Elimina">
                          <ActionIcon variant="subtle" color="red" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); handleDelete(r); }}>
                            <IconTrash size={isMobile ? 14 : 16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      {/* Modal form */}
      <Modal
        opened={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={<Text fw={600}>{editingRecord ? 'Modifica compravendita' : 'Nuova compravendita'}</Text>}
        size={isMobile ? '100%' : 'xl'}
        fullScreen={isMobile}
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="sm">
            {/* Mese */}
            <MonthPickerInput
              label="Mese di riferimento"
              placeholder="Seleziona mese"
              value={form.mese_riferimento}
              onChange={v => setForm(f => ({ ...f, mese_riferimento: v }))}
              required
              locale="it"
            />

            <Divider label="Veicolo" labelPosition="left" />
            <Grid>
              <Grid.Col span={{ base: 6, sm: 3 }}>
                <TextInput label="Targa" value={form.targa} onChange={e => setForm(f => ({ ...f, targa: e.target.value }))} />
              </Grid.Col>
              <Grid.Col span={{ base: 6, sm: 3 }}>
                <TextInput label="Telaio" value={form.telaio} onChange={e => setForm(f => ({ ...f, telaio: e.target.value }))} />
              </Grid.Col>
              <Grid.Col span={{ base: 6, sm: 3 }}>
                <TextInput label="Marca" value={form.marca} onChange={e => setForm(f => ({ ...f, marca: e.target.value }))} />
              </Grid.Col>
              <Grid.Col span={{ base: 6, sm: 3 }}>
                <TextInput label="Modello" value={form.modello} onChange={e => setForm(f => ({ ...f, modello: e.target.value }))} />
              </Grid.Col>
            </Grid>

            <Divider label="Anagrafica" labelPosition="left" />
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Venditore (denominazione)" value={form.venditore_denominazione} onChange={e => setForm(f => ({ ...f, venditore_denominazione: e.target.value }))} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Acquirente (denominazione)" value={form.acquirente_denominazione} onChange={e => setForm(f => ({ ...f, acquirente_denominazione: e.target.value }))} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <TextInput label="Acquirente — Indirizzo" value={form.acquirente_indirizzo} onChange={e => setForm(f => ({ ...f, acquirente_indirizzo: e.target.value }))} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput label="CF / P.IVA" value={form.acquirente_cf_piva} onChange={e => setForm(f => ({ ...f, acquirente_cf_piva: e.target.value }))} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput label="Acquirente — Cellulare" value={form.acquirente_cellulare} onChange={e => setForm(f => ({ ...f, acquirente_cellulare: e.target.value }))} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <TextInput label="Acquirente — E-mail" value={form.acquirente_email} onChange={e => setForm(f => ({ ...f, acquirente_email: e.target.value }))} />
              </Grid.Col>
            </Grid>

            <Divider label="Prezzi (IVA esclusa)" labelPosition="left" />
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Group gap={4} align="flex-end">
                  <NumberInput
                    label="Prezzo acquisto veicolo"
                    value={form.prezzo_acquisto_veicolo}
                    onChange={v => setForm(f => ({ ...f, prezzo_acquisto_veicolo: v || 0 }))}
                    prefix="€ " decimalScale={2} hideControls thousandSeparator="."
                    decimalSeparator=","
                    style={{ flex: 1 }}
                  />
                  <ReconcIcon
                    items={form.riconciliazioni}
                    fieldRef="prezzo_acquisto_veicolo"
                    onToggle={toggleReconc}
                  />
                </Group>
                <IvaHint value={form.prezzo_acquisto_veicolo} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Group gap={4} align="flex-end">
                  <NumberInput
                    label="Prezzo vendita veicolo"
                    value={form.prezzo_vendita_veicolo}
                    onChange={v => setForm(f => ({ ...f, prezzo_vendita_veicolo: v || 0 }))}
                    prefix="€ " decimalScale={2} hideControls thousandSeparator="."
                    decimalSeparator=","
                    style={{ flex: 1 }}
                  />
                  <ReconcIcon
                    items={form.riconciliazioni}
                    fieldRef="prezzo_vendita_veicolo"
                    onToggle={toggleReconc}
                  />
                </Group>
                <IvaHint value={form.prezzo_vendita_veicolo} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Group gap={4} align="flex-end">
                  <NumberInput
                    label="Prezzo acquisto pratiche auto"
                    value={form.prezzo_acquisto_pratiche}
                    onChange={v => setForm(f => ({ ...f, prezzo_acquisto_pratiche: v || 0 }))}
                    prefix="€ " decimalScale={2} hideControls thousandSeparator="."
                    decimalSeparator=","
                    style={{ flex: 1 }}
                  />
                  <ReconcIcon
                    items={form.riconciliazioni}
                    fieldRef="prezzo_acquisto_pratiche"
                    onToggle={toggleReconc}
                  />
                </Group>
                <IvaHint value={form.prezzo_acquisto_pratiche} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Group gap={4} align="flex-end">
                  <NumberInput
                    label="Prezzo vendita pratiche auto"
                    value={form.prezzo_vendita_pratiche}
                    onChange={v => setForm(f => ({ ...f, prezzo_vendita_pratiche: v || 0 }))}
                    prefix="€ " decimalScale={2} hideControls thousandSeparator="."
                    decimalSeparator=","
                    style={{ flex: 1 }}
                  />
                  <ReconcIcon
                    items={form.riconciliazioni}
                    fieldRef="prezzo_vendita_pratiche"
                    onToggle={toggleReconc}
                  />
                </Group>
                <IvaHint value={form.prezzo_vendita_pratiche} />
              </Grid.Col>
            </Grid>

            <Divider label="Costi e provvigioni (IVA esclusa)" labelPosition="left" />
            <Grid>
              <Grid.Col span={12}>
                <Group gap={4} align="flex-end">
                  <NumberInput
                    label="Costi accessori (garanzie, manutenzione, riparazione)"
                    value={form.costi_accessori}
                    onChange={v => setForm(f => ({ ...f, costi_accessori: v || 0 }))}
                    prefix="€ " decimalScale={2} hideControls thousandSeparator="."
                    decimalSeparator=","
                    style={{ flex: 1 }}
                  />
                  <ReconcIcon
                    items={form.riconciliazioni}
                    fieldRef="costi_accessori"
                    onToggle={toggleReconc}
                  />
                </Group>
                <IvaHint value={form.costi_accessori} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Group gap={4} align="flex-end">
                  <NumberInput
                    label="Provvigioni attive"
                    value={form.provvigioni_attive}
                    onChange={v => setForm(f => ({ ...f, provvigioni_attive: v || 0 }))}
                    prefix="€ " decimalScale={2} hideControls thousandSeparator="."
                    decimalSeparator=","
                    style={{ flex: 1 }}
                  />
                  <ReconcIcon
                    items={form.riconciliazioni}
                    fieldRef="provvigioni_attive"
                    onToggle={toggleReconc}
                  />
                </Group>
                <IvaHint value={form.provvigioni_attive} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Group gap={4} align="flex-end">
                  <NumberInput
                    label="Provvigioni passive"
                    value={form.provvigioni_passive}
                    onChange={v => setForm(f => ({ ...f, provvigioni_passive: v || 0 }))}
                    prefix="€ " decimalScale={2} hideControls thousandSeparator="."
                    decimalSeparator=","
                    style={{ flex: 1 }}
                  />
                  <ReconcIcon
                    items={form.riconciliazioni}
                    fieldRef="provvigioni_passive"
                    onToggle={toggleReconc}
                  />
                </Group>
                <IvaHint value={form.provvigioni_passive} />
              </Grid.Col>
            </Grid>

            {/* Margine calcolato */}
            <Card withBorder radius="sm" p="xs" style={{ backgroundColor: '#f8f9fa' }}>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Margine netto stimato</Text>
                <Text fw={700} c={calcMargine(form) >= 0 ? 'teal' : 'red'}>
                  {formatAmount(calcMargine(form))}
                </Text>
              </Group>
            </Card>

            <Divider
              label={
                <Group gap="xs">
                  <Text size="sm">Finanziamento</Text>
                  <Switch
                    size="xs"
                    checked={form.finanziamento_attivo}
                    onChange={e => setForm(f => ({ ...f, finanziamento_attivo: e.currentTarget.checked }))}
                    label={form.finanziamento_attivo ? 'Presente' : 'Assente'}
                    color="teal"
                  />
                </Group>
              }
              labelPosition="left"
            />
            {form.finanziamento_attivo && (
              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Finanziaria"
                    placeholder="Nome finanziaria"
                    value={form.finanziaria}
                    onChange={e => setForm(f => ({ ...f, finanziaria: e.target.value }))}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap={4} align="flex-end">
                    <NumberInput
                      label="Importo Finanziato"
                      value={form.importo_finanziato}
                      onChange={v => setForm(f => ({ ...f, importo_finanziato: v || 0 }))}
                      prefix="€ " decimalScale={2} hideControls thousandSeparator="."
                      decimalSeparator=","
                      style={{ flex: 1 }}
                    />
                    <ReconcIcon
                      items={form.riconciliazioni}
                      fieldRef="importo_finanziato"
                      onToggle={toggleReconc}
                    />
                  </Group>
                </Grid.Col>
              </Grid>
            )}

            <Divider label="Note" labelPosition="left" />
            <Textarea
              placeholder="Note aggiuntive..."
              value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              rows={6}
            />

            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => { setShowModal(false); resetForm(); }}>Annulla</Button>
              <Button type="submit" color="teal">{editingRecord ? 'Aggiorna' : 'Salva'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
